/**
 * GMA-1628 (CompassCare Web 13/14 — Schedule / Activities) — pure model.
 *
 * Activity-type catalog, the status system (RED reserved for "missed"), status
 * derivation from schedule vs now, and recurring-series edit planning that
 * enforces the GMA-1611 invariant: a past COMPLETED occurrence is never
 * editable/deletable (under any edit scope).
 *
 * All functions fail safe: bad input yields a benign result, never a throw.
 */

export const ACTIVITY_TYPES = Object.freeze({
  medication: Object.freeze({ label: "Medication reminder" }),
  day_program: Object.freeze({ label: "Day program" }),
  wellness_check: Object.freeze({ label: "Wellness check" }),
  transportation: Object.freeze({ label: "Transportation" }),
  appointment: Object.freeze({ label: "Appointment" }),
  quiet_time: Object.freeze({ label: "Quiet time" }),
  evening_routine: Object.freeze({ label: "Evening routine" }),
  meal: Object.freeze({ label: "Meal" }),
  hygiene: Object.freeze({ label: "Hygiene" }),
  hydration: Object.freeze({ label: "Hydration" }),
  sleep_routine: Object.freeze({ label: "Sleep routine" }),
});

/**
 * The only safe way to read ACTIVITY_TYPES by key. Same hazard as
 * intelModel's watchTypeFor: `ACTIVITY_TYPES["constructor"]` is truthy, so a
 * `TABLE[key] ? … : fallback` guard silently takes the found branch and the
 * caller renders `Object`'s shape instead of falling back to the raw key.
 *
 * @returns {object|null} the frozen type entry, or null when unmapped.
 */
export function activityTypeFor(key) {
  if (typeof key !== "string" || !key) return null;
  return Object.prototype.hasOwnProperty.call(ACTIVITY_TYPES, key)
    ? ACTIVITY_TYPES[key]
    : null;
}

export const ACTIVITY_STATUSES = Object.freeze([
  "completed",
  "upcoming",
  "missed",
  "needs_follow_up",
]);

// Status -> severity token. RED (critical) is reserved for "missed"; a neutral
// (null) status has no severity tint.
export const STATUS_SEVERITY = Object.freeze({
  completed: "normal", // green
  upcoming: null, // neutral
  missed: "critical", // red — reserved
  needs_follow_up: "caution", // yellow
});

const isNum = (v) => typeof v === "number" && Number.isFinite(v);

/**
 * Derive an activity's status from its completion + schedule relative to now.
 *   completed -> "completed"; explicitly flagged -> "needs_follow_up";
 *   future & not done -> "upcoming"; past & not done -> "missed".
 * @param {{completed?:boolean, needsFollowUp?:boolean, startMs?:number, endMs?:number}} activity
 * @param {number} nowMs
 * @returns {"completed"|"upcoming"|"missed"|"needs_follow_up"}
 */
export function deriveActivityStatus(activity, nowMs) {
  if (!activity || typeof activity !== "object") return "upcoming";
  // strict boolean — a stray "false"/0/1 must not be read as completed/follow-up
  if (activity.completed === true) return "completed";
  if (activity.needsFollowUp === true) return "needs_follow_up";
  // Fail-safe on malformed intervals (Copilot #308): if endMs < startMs the
  // later of the two is the due time, so a future activity with a corrupt end
  // can never classify as "missed".
  const due =
    isNum(activity.endMs) && isNum(activity.startMs)
      ? Math.max(activity.endMs, activity.startMs)
      : isNum(activity.endMs)
        ? activity.endMs
        : activity.startMs;
  if (isNum(due) && isNum(nowMs) && due < nowMs) return "missed";
  return "upcoming";
}

/**
 * Plan which occurrences of a recurring series an edit may touch. Returns two
 * id lists: `editable` (the edit may modify these) and `protectedIds` (the edit
 * will NOT touch these — a past completed occurrence per GMA-1611, OR an
 * occurrence outside the chosen scope). Occurrences with no valid string `id`
 * are skipped entirely (never emitted as `undefined`). NOTE: the key is
 * `protectedIds`, not `protected` — `protected` is a reserved word, so while
 * the property could be aliased (`const { protected: p } = …`), it cannot be
 * destructured under its own name in strict mode; `protectedIds` avoids the
 * footgun entirely.
 * GMA-1611: a PAST COMPLETED occurrence is always protected (never editable),
 * regardless of scope.
 *   - scope "series": every non-(past-completed) occurrence is editable.
 *   - scope "this": only `targetId` is editable — unless it is past-completed.
 * @param {Array<{id,startMs,completed}>} occurrences
 * @param {{scope?:"series"|"this", targetId?:string, nowMs?:number}|null} [opts]
 *   Null/invalid opts are tolerated (fail-safe); a missing/invalid nowMs means
 *   NO occurrence can be confirmed-future, so every completed occurrence is
 *   protected.
 * @returns {{editable:string[], protectedIds:string[]}}
 */
export function planSeriesEdit(occurrences, opts) {
  const list = Array.isArray(occurrences) ? occurrences : [];
  const o = opts && typeof opts === "object" ? opts : {}; // fail-safe: null opts
  const scope = o.scope === "this" ? "this" : "series";
  const nowMs = isNum(o.nowMs) ? o.nowMs : null;
  const editable = [];
  const protectedIds = [];
  for (const occ of list) {
    // skip malformed occurrences — an id-less row can't be acted on and must
    // not leak an `undefined` into either list.
    if (!occ || typeof occ !== "object") continue;
    if (typeof occ.id !== "string" || !occ.id) continue;
    // GMA-1611 (fail-safe): a completed occurrence is protected whenever we
    // cannot confirm it is in the FUTURE (unknown nowMs / startMs, or past) —
    // never risk editing a completed occurrence on ambiguous timing.
    // Strictly AFTER now — an occurrence starting exactly at nowMs has already
    // begun and is not confirmed-future (Copilot #308; fail-safe leans protected).
    const confirmedFuture =
      nowMs !== null && isNum(occ.startMs) && occ.startMs > nowMs;
    const protectedCompleted = occ.completed === true && !confirmedFuture;
    const targeted = scope === "series" ? true : occ.id === o.targetId;
    if (!protectedCompleted && targeted) editable.push(occ.id);
    else protectedIds.push(occ.id);
  }
  return { editable, protectedIds };
}

/**
 * Summarize one day's activities for the Schedule right rail: total count +
 * per-status counts (Done / Missed / Follow-up / Upcoming). Statuses are
 * DERIVED through deriveActivityStatus — the single status authority — never
 * read from stored flags, so the rail can't disagree with the agenda chips.
 * Fail-safe: non-array input or malformed rows yield a benign zeroed summary.
 * @param {Array<{completed?:boolean, needsFollowUp?:boolean, startMs?:number, endMs?:number}>} activities
 * @param {number} nowMs
 * @returns {{total:number, counts:{completed:number, upcoming:number, missed:number, needs_follow_up:number}}}
 */
export function summarizeDay(activities, nowMs) {
  const counts = {};
  for (const status of ACTIVITY_STATUSES) counts[status] = 0;
  const list = Array.isArray(activities) ? activities : [];
  let total = 0;
  for (const act of list) {
    if (!act || typeof act !== "object") continue; // skip malformed rows
    total += 1;
    counts[deriveActivityStatus(act, nowMs)] += 1;
  }
  return { total, counts };
}
